import moment from "moment";
import {AdministrationStore} from "../AdministrationStore";
import {BlockUI} from "Core/Common/BlockUi";

import {LABELS} from "Core/Components/Translation/Locales";

import {ActiveSessionsDto, EnvironmentDto} from "./EnvironmentDto";
import {ActiveSessionsPopup} from "./ActiveSessionsPopup";

import {FormatConverter} from 'FormatEditor/FormatConverter';
import {DATE_FORMATS} from "Core/Constants/DateTimeFormats";
import {ConfirmationDialog, EVENTS, Types} from "Core/Components/Dialogs/ConfirmationDialog/ConfirmationDialog";
import {Notifier} from "Core/Common/Notifier";
import {P} from "Core/Common/Promise";

import Template from "Pages/AdministrationPage/Tabs/Environment.html";
import * as ko from "knockout";
import 'jquery';
import 'xEditable';

ko.templates["Pages/AdministrationPage/Tabs/Environment"] = Template;

ko.bindingHandlers['notesEditor'] = {
    init: (element, valueAccessor, allBindings, viewModel, bindingContext) => {
        const environmentDto: EnvironmentDto = ko.unwrap(valueAccessor());
        const tab: Environment = bindingContext.$parent;

        ($.fn.editable as any).defaults.mode = 'popup';
        $(element).editable({
            type: 'textarea',
            placement: 'left',
            showbuttons: 'bottom',
            emptytext: 'Empty',
            rows: 4,
            value: environmentDto.Notes || '',
            validate: (value: string) => {
                if (value && value.length > 255) {
                    return 'Notes cannot be longer than 255 characters';
                }
            },
            success: (response, newValue) => {
                const previousNotes = environmentDto.Notes || '';

                tab.SaveNotes(environmentDto, newValue)
                    .fail(() => $(element).editable('setValue', previousNotes));
            }
        });

        ko.utils.domNodeDisposal.addDisposeCallback(element, () => $(element).editable('destroy'));
    }
};

export class Environment {
    private _environments: KnockoutObservableArray<EnvironmentDto>;
    private _environmentsWithoutChanges: KnockoutObservableArray<EnvironmentDto>;
    private _searchText: KnockoutObservable<string>;
    private _sortColumn: KnockoutObservable<string>;
    private _sortDescending: KnockoutObservable<boolean>;

    private _labels = LABELS;

    constructor(){
        this._environments = ko.observableArray([]);
        this._environmentsWithoutChanges = ko.observableArray([]);
        this._searchText = ko.observable('');
        this._sortColumn = ko.observable(null);
        this._sortDescending = ko.observable(false);

        this.LoadData();
    }

    GetTemplateName(){
        return 'Pages/AdministrationPage/Tabs/Environment';
    }

    LoadData(){
        BlockUI.Block();
        AdministrationStore.GetEnvironments().always(()=>{
            BlockUI.Unblock();
        }).then((data)=>{
            this._environments(data);
            this._environmentsWithoutChanges(data.slice());
            this.Search();
        });
    }

    AfterRender(){

    }

    FormatDate(value: string){
        if(!value){
            return value;
        }
        return moment(FormatConverter.CorrectTimezone(value)).format(DATE_FORMATS.SAVE_FORMAT_FULL.MomentFormat);
    }

    ParseDate(dateString: string): Date | null {
        if (dateString === null) {
            return null;
        }

        const date = new Date(dateString);

        if (isNaN(date.getTime())) {
            return null;
        }

        return date;
    }

    ShowSessionUsers(environmentDto: EnvironmentDto, session: ActiveSessionsDto){
        new ActiveSessionsPopup(environmentDto.Id, session.ApiClient).Show();
    }

    MarkForDeletion(environmentDto: EnvironmentDto){
        const confirmationDialog = new ConfirmationDialog({
            Text: 'Are you sure? The system will be deleted in 3 days',
            Type: Types.Question
        });

        confirmationDialog.On(EVENTS.CONFIRM_SELECTED, this, () => {
            BlockUI.Block();
            AdministrationStore.MarkForDeletion(environmentDto.Id)
            .always(()=>BlockUI.Unblock())
            .then(()=>this.LoadData());
        });

        confirmationDialog.Show();
    }

    Delete(environmentDto: EnvironmentDto){
        BlockUI.Block();
        AdministrationStore.Delete(environmentDto.Id)
            .always(()=>BlockUI.Unblock())
            .then(()=>this._environments.remove(environmentDto));
    }

    SaveNotes(environmentDto: EnvironmentDto, notes: string): P.Promise<null> {
        const deferred = P.defer<null>();

        AdministrationStore.UpdateNotes(environmentDto.Id, notes || '')
            .then(() => {
                environmentDto.Notes = notes;
                deferred.resolve(null);
            })
            .fail(error => {
                Notifier.Failed(error && error.message ? error.message : `Failed to save notes for ${environmentDto.DatabaseName}`);
                deferred.reject(error);
            });

        return deferred.promise();
    }

    UnmarkForDeletion(environmentDto: EnvironmentDto){
        BlockUI.Block();
        AdministrationStore.UnmarkForDeletion(environmentDto.Id)
        .always(()=>BlockUI.Unblock())
        .then(()=>this.LoadData());
    }

    Search() {
        let result = [];
        const searchValue = this._searchText().toLowerCase().trim();

        _.each(this._environmentsWithoutChanges(), row => {
            if (row.Url.toLowerCase().includes(searchValue) || row.DatabaseName.toLowerCase().includes(searchValue)) {
                result.push(row);
            }
        });

        this._environments(result);
        this.ApplySort();
    }

    Sort = (column: string): void => {
        if (this._sortColumn() === column) {
            this._sortDescending(!this._sortDescending());
        } else {
            this._sortColumn(column);
            this._sortDescending(false);
        }

        this.ApplySort();
    }

    SortIcon = (column: string): string => {
        if (this._sortColumn() !== column) return 'fa-sort sort-icon-inactive';
        return this._sortDescending() ? 'fa-sort-desc' : 'fa-sort-asc';
    }

    private ApplySort() {
        if (!this._sortColumn()) {
            return;
        }

        this._environments.sort((a, b) => this.Compare(this._sortColumn(), a, b, this._sortDescending()));
    }

    private Compare(column: string, a: EnvironmentDto, b: EnvironmentDto, descending: boolean): number {
        const aValue = this.ResolveSortValue(column, a);
        const bValue = this.ResolveSortValue(column, b);

        if (aValue === null && bValue !== null) {
            return 1;
        }
        if (aValue !== null && bValue === null) {
            return -1;
        }
        if (aValue === null && bValue === null) {
            return 0;
        }

        let result: number;
        if (aValue instanceof Date && bValue instanceof Date) {
            result = aValue.getTime() - bValue.getTime();
        } else if (typeof aValue === "number" && typeof bValue === "number") {
            result = aValue - bValue;
        } else {
            result = String(aValue).localeCompare(String(bValue));
        }

        return descending ? -result : result;
    }

    private ResolveSortValue(column: string, row: EnvironmentDto): any {
        const value = row[column];

        if (column === "Size") {
            return value ? parseFloat(value) : null;
        }

        if (column === "LastLoginDate") {
            return this.ParseDate(value);
        }

        if (column === "ActiveSessionCount") {
            return typeof value === "number" ? value : null;
        }

        if (column === "IsMarkedForDeletion") {
            return Number(value);
        }

        return value ? value : null;
    }
}
